// src/pages/chat/ChatHeader.jsx
import React from 'react';
import { Menu, Bot, Sparkles, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ChatHeader = ({
  setSidebarOpen,
  remainingMessages,
  isGuest,
  user,
  onUpgrade
}) => {
  const { t } = useTranslation();
  const totalLimit = isGuest ? 3 : 10;

  const displayedRemaining = isGuest
    ? Math.min(remainingMessages ?? 3, 3)
    : remainingMessages;
  return (
    <header className="bg-white shadow-sm border-b border-gray-200 px-4 md:px-6 py-3">
      <div className="flex items-center justify-between">
        {/* Left: Menu + Logo */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg flex items-center justify-center shadow-md">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-gray-800">{t('chatbot.header.ai_assistant')}</h1>
              <div className="flex items-center gap-2 text-xs">
                <div className="flex items-center gap-1 text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>{t('chatbot.header.ready')}</span>
                </div>
                {isGuest ? (
                  <span className="text-amber-700 font-medium">• {t('chatbot.header.guest_mode')}</span>
                ) : (
                  <span className="text-blue-600 font-medium">• {user?.name}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Message counter */}
          <div className="hidden sm:flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full">
            <span className="text-xs font-medium text-gray-700">
              {displayedRemaining !== null ? `${displayedRemaining}/${totalLimit}` : '--/--'}
            </span>
            <span className="text-xs text-gray-500">{t('chatbot.header.messages_left')}</span>
          </div>

          {/* Upgrade / Unlock Button – ALWAYS VISIBLE */}
          <button
            onClick={onUpgrade}
            className={`flex items-center gap-1.5 px-4 py-2 text-white text-sm font-semibold rounded-full transition-all hover:shadow-lg ${isGuest
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
              : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700'
              }`}
          >
            {isGuest ? <Sparkles className="w-4 h-4" /> : <Crown className="w-4 h-4" />}
            {isGuest ? t('chatbot.header.unlock') : t('chatbot.header.upgrade')}
          </button>

          {/* Disclaimer */}
          <div className="hidden lg:flex items-center gap-2 text-xs text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full font-medium">
            <Sparkles className="w-3 h-3" />
            {t('chatbot.header.disclaimer')}
          </div>
        </div>
      </div>

      {/* Mobile counter */}
      <div className="sm:hidden mt-3 flex justify-center">
        <div className="bg-gray-100 px-4 py-2 rounded-full text-xs font-medium">
          <span className="text-gray-700">
            {remainingMessages !== null ? `${remainingMessages}/${totalLimit}` : '--/--'}
          </span>
          <span className="text-gray-500 ml-1">{t('chatbot.header.messages_left')}</span>
        </div>
      </div>
    </header>
  );
};

export default ChatHeader;